import { Code, Settings } from 'lucide-react'
import { useEffect, useState } from 'react'

/**
 * Loading animation component for first-time script creation
 * Shows spinning gears and a progress bar for 5 seconds
 */
function ClusterGenerationLoader() {
  const [dots, setDots] = useState('')
  const [progress, setProgress] = useState(0)

  // Define animation styles as objects
  const gearSpinStyle = {
    animation: 'rotate 8s linear infinite',
  }

  const gearSpinReverseStyle = {
    animation: 'rotateReverse 6s linear infinite',
  }

  // Define keyframes as a string to be injected into the document head
  useEffect(() => {
    // Create and inject keyframes
    const styleEl = document.createElement('style')
    styleEl.type = 'text/css'
    styleEl.innerHTML = `
      @keyframes rotate {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      @keyframes rotateReverse {
        from { transform: rotate(0deg); }
        to { transform: rotate(-360deg); }
      }
    `
    document.head.appendChild(styleEl)

    // Animate the loading dots
    const dotsInterval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : `${prev}.`)
    }, 500)

    // Progress bar animation
    const startTime = Date.now()
    const duration = 5000 // 5 seconds

    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime
      const newProgress = Math.min(100, (elapsed / duration) * 100)
      setProgress(newProgress)

      if (elapsed >= duration) {
        clearInterval(progressInterval)
      }
    }, 50)

    return () => {
      clearInterval(dotsInterval)
      clearInterval(progressInterval)
      // Clean up the style element
      document.head.removeChild(styleEl)
    }
  }, [])

  return (
    <div className={`
      flex min-h-[60vh] w-full flex-col items-center justify-center bg-slate-50
    `}
    >
      <div className="relative">
        {/* Large central gear */}
        <div
          className={`
            flex h-24 w-24 items-center justify-center rounded-full border-4
            border-slate-300
          `}
          style={gearSpinStyle}
        >
          <Settings className="h-16 w-16 text-slate-700" />
        </div>

        {/* Satellite gears */}
        <div
          className="absolute -top-6 -right-6"
          style={gearSpinReverseStyle}
        >
          <Settings className="h-10 w-10 text-slate-600" />
        </div>
        <div
          className="absolute -bottom-6 -left-6"
          style={gearSpinReverseStyle}
        >
          <Settings className="h-10 w-10 text-slate-600" />
        </div>
        <div
          className="absolute -top-6 -left-6"
          style={gearSpinStyle}
        >
          <Code className="h-10 w-10 text-slate-600" />
        </div>
        <div
          className="absolute -right-6 -bottom-6"
          style={gearSpinStyle}
        >
          <Code className="h-10 w-10 text-slate-600" />
        </div>
      </div>

      <div className="mx-auto mt-8 w-full max-w-md px-4 text-center">
        <h3 className="mb-2 text-xl font-bold text-slate-800">
          Generating Code Execution Cluster
          {dots}
        </h3>
        <p className="mb-4 text-slate-600">
          Initializing runtime environment and connecting to execution nodes...
        </p>
        <div
          className="mx-auto w-full overflow-hidden rounded-full bg-slate-200"
          style={{ maxWidth: '280px' }}
        >
          <div
            className={`
              h-2 rounded-full bg-black transition-all duration-100 ease-out
            `}
            style={{ width: `${progress}%` }}
          >
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClusterGenerationLoader
